<template>
  <div class="file-preview-overlay" @click="close">
    <div class="file-preview-container" @click.stop>
      <div class="file-preview-header">
        <div class="header-title">
          <span class="attachment-info" :data-tooltip="fileName">{{ fileName }}</span>
        </div>
        <div v-if="isImage || isPdf || isExcel" class="controls">
          <div v-if="isImage" class="image-center-controls">
            <button @click="rotateLeft" class="control-btn" title="Rotate Left">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
              </svg>
            </button>
            <button @click="rotateRight" class="control-btn" title="Rotate Right">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" transform="scale(-1,1) translate(-24,0)"/>
              </svg>
            </button>
            <button @click="zoomOut" class="control-btn" title="Zoom Out">
              <i class="el-icon-zoom-out"></i>
            </button>
            <button @click="zoomIn" class="control-btn" title="Zoom In">
              <i class="el-icon-zoom-in"></i>
            </button>
          </div>
          <div v-if="isPdf" class="pdf-center-controls">
            <button @click="zoomOut" class="control-btn" title="Zoom Out">
              <i class="el-icon-zoom-out"></i>
            </button>
            <button @click="zoomIn" class="control-btn" title="Zoom In">
              <i class="el-icon-zoom-in"></i>
            </button>
            <button @click="prevPage" class="control-btn" title="Previous Page">
              <i class="el-icon-arrow-left"></i>
            </button>
            <input 
              type="number" 
              v-model.number="currentPage" 
              :min="1" 
              :max="totalPages"
              @change="scrollToPage(currentPage)"
              class="page-input"
            /> / {{ totalPages }}
            <button @click="nextPage" class="control-btn" title="Next Page">
              <i class="el-icon-arrow-right"></i>
            </button>
          </div>
          <div v-if="isExcel" class="excel-center-controls">
            <select v-model="currentSheetName" @change="loadSheetData" class="sheet-selector">
              <option v-for="(sheetName, index) in sheetNames" :key="index" :value="sheetName">
                {{ sheetName }}
              </option>
            </select>
          </div>
        </div>
        <div class="header-right">
          <button @click="downloadFile" class="text-control-btn">
            <i class="el-icon-download"></i>
            DOWNLOAD
          </button>
          <button v-if="isImage || isPdf" @click="printFile" class="text-control-btn">
            <i class="el-icon-printer"></i>
            PRINT
          </button>
          <button @click="close" class="text-control-btn">
            <i class="el-icon-close"></i>
            CLOSE
          </button>
        </div>
      </div>
      <div class="file-preview-body">
        
        <div v-if="!fileUrl" class="debug-info">
          No file URL provided
        </div>
        
        <div v-if="isImage" class="image-container">
          <img :src="fileUrl" class="preview-image" :style="imageStyle" />
        </div>
        
        <div v-if="isPdf" class="pdf-container">
          <div class="pdf-scroll-container" ref="pdfContainer">
            <canvas
              v-for="page in totalPages"
              :key="page"
              :ref="'pdfCanvas_' + page"
              class="pdf-page-canvas"
            ></canvas>
          </div>
        </div>
        <div v-if="isDocx" class="unsupported-file">
          <i class="el-icon-document" style="font-size: 64px; color: #3498db; margin-bottom: 20px;"></i>
          <p style="font-size: 18px; font-weight: 500;">{{ fileName }}</p>
          <p>Word documents cannot be previewed in browser</p>
          <button @click="downloadFile" class="download-btn">Download to View</button>
        </div>
        
        <!-- Excel -->
        <div v-if="isExcel" class="excel-container">
          <div v-if="excelData.length === 0 && sheetNames.length === 0" class="unsupported-file">
            <p>Unable to preview Excel file</p>
            <p>The file may be corrupted or in an unsupported format</p>
            <button @click="downloadFile" class="download-btn">Download File</button>
          </div>
          <div v-else-if="excelData.length === 0" class="debug-info">
            Loading Excel data...
            <br><small>Sheets: {{ sheetNames.join(', ') }}</small>
            <br><small>Current: {{ currentSheetName }}</small>
          </div>
          <table v-else class="excel-table">
            <tr v-for="(row, rowIndex) in excelData" :key="rowIndex">
              <td v-for="(cell, colIndex) in row" :key="colIndex">{{ cell || '' }}</td>
            </tr>
          </table>
        </div>
        
        <!-- Fallback for unknown file types -->
        <div v-if="fileUrl && !isImage && !isPdf && !isExcel && !isDocx" class="unsupported-file">
          <p>File type not supported for preview</p>
          <p>Extension: {{ fileExtension }}</p>
          <button @click="downloadFile" class="download-btn">Download File</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
const pdfjsLib = require('../utils/pdfWorker')

export default {
  name: 'FilePreview',
  components: {},
  props: {
    fileUrl: {
      type: String,
      default: ''
    },
    originalFileName: {
      type: String,
      default: ''
    }
  },
  emits: ['update:visible'],
  data() {
    return {
      rotation: 0,
      scale: 1,
      pdfScale: 2.5,
      workbook: null,
      excelData: [],
      sheetNames: [],
      currentSheetName: '',
      currentPage: 1,
      totalPages: 0,
      pdfDoc: null
    }
  },
  computed: {
    isBlobUrl() {
      return this.fileUrl && this.fileUrl.startsWith('blob:')
    },
    fileExtension() {
     if (!this.fileUrl) return ''
      if (this.isBlobUrl) {
        if (this.originalFileName) {
          return this.originalFileName.slice(((this.originalFileName.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase()
        }
        return ''
      }
      return this.fileUrl.split('?')[0].split('.').pop().toLowerCase()
    },
    fileName() {
      if (this.originalFileName) return this.originalFileName
      if (!this.fileUrl || this.isBlobUrl) return 'preview'
      return this.fileUrl.split('/').pop().split('?')[0]
    },
    isImage() {
      if (this.isBlobUrl && !this.fileExtension) return true
      return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(this.fileExtension)
    },
    isPdf() {
      return this.fileExtension === 'pdf'
    },
    isExcel() {
      return ['xlsx', 'xls'].includes(this.fileExtension)
    },
    isDocx() {
      return this.fileExtension === 'docx' || this.fileExtension === 'doc'
    },
    imageStyle() {
      return {
        transform: `rotate(${this.rotation}deg) scale(${this.scale})`,
        transition: 'transform 0.3s ease-in-out'
      }
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    handleKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault()
        this.close()
        return
      }
      if (this.isPdf) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault()
          this.prevPage()
        } else if (e.key === 'ArrowRight') {
          e.preventDefault()
          this.nextPage()
        }
      }
      
      if (e.key === '+' || e.key === '=') {
        e.preventDefault()
        this.zoomIn()
      } else if (e.key === '-') {
        e.preventDefault()
        this.zoomOut()
      } else if (e.key === '0') {
        e.preventDefault()
        this.resetZoom()
      }
      
      if (this.isImage) {
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          this.rotateRight()
        } else if (e.key === 'ArrowDown') {
          e.preventDefault()
          this.rotateLeft()
        } else if (e.key === 'r' || e.key === 'R') {
          e.preventDefault()
          this.rotateRight()
        } else if (e.key === 'l' || e.key === 'L') {
          e.preventDefault()
          this.rotateLeft()
        }
      }
    },
    rotateLeft() {
      this.rotation -= 90
    },
    rotateRight() {
      this.rotation += 90
    },
    zoomIn() {
      if (this.isImage) {
        this.scale = Math.min(this.scale * 1.2, 3)
      } else if (this.isPdf) {
        this.pdfScale = Math.min(this.pdfScale * 1.2, 3)
        this.reRenderAllPages()
      }
    },
    zoomOut() {
      if (this.isImage) {
        this.scale = Math.max(this.scale / 1.2, 0.5)
      } else if (this.isPdf) {
        this.pdfScale = Math.max(this.pdfScale / 1.2, 0.5)
        this.reRenderAllPages()
      }
    },
    resetZoom() {
      if (this.isImage) {
        this.scale = 1
      } else if (this.isPdf) {
        this.pdfScale = 1.5
        this.reRenderAllPages()
      }
    },
    downloadFile() {
      const link = document.createElement('a')
      link.href = this.fileUrl
      link.download = this.fileName
      link.click()
    },
    printFile() {
       window.open(this.fileUrl, '_blank')
    },
    async loadExcelData() {
      try {
        const response = await fetch(this.fileUrl)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const arrayBuffer = await response.arrayBuffer()        
        this.workbook = XLSX.read(arrayBuffer, { type: 'array' })
        this.sheetNames = this.workbook.SheetNames        
        if (this.sheetNames.length > 0) {
          this.currentSheetName = this.sheetNames[0]
          this.loadSheetData()
        }
      } catch (error) {
        this.excelData = [['Error loading Excel file: ' + error.message]]
      }
    },
    loadSheetData() {
      if (!this.currentSheetName || !this.sheetNames.includes(this.currentSheetName) || !this.workbook) {
        return
      }
      try {
        const sheet = this.workbook.Sheets[this.currentSheetName]
        if (!sheet) {
          return
        }
        this.excelData = XLSX.utils.sheet_to_json(sheet, { header: 1 })
      } catch (error) {
        console.error('Error loading sheet data:', error)
        this.excelData = [['Error loading sheet: ' + error.message]]
      }
    },
    async loadPdfData() {
      try {
        const loadingTask = pdfjsLib.getDocument(this.fileUrl);
        this.pdfDoc = await loadingTask.promise;
        this.totalPages = this.pdfDoc.numPages;
        await this.$nextTick();
        await this.renderAllPages();
        this.observePages();
      } catch (error) {
        console.error('Error loading PDF:', error);
      }
    },
    async renderAllPages() {
      if (!this.pdfDoc) return;
      for (let i = 1; i <= this.totalPages; i += 1) {
        const page = await this.pdfDoc.getPage(i);
        const viewport = page.getViewport({ scale: this.pdfScale });
        const canvas = this.$refs[`pdfCanvas_${i}`][0];
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        await page.render({ canvasContext: context, viewport }).promise;
      }
    },
    observePages() {
      if (this._observer) this._observer.disconnect();
      this._observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.currentPage = parseInt(entry.target.dataset.page, 10);
          }
        });
      }, { root: this.$refs.pdfContainer, threshold: 0.5 });
      for (let i = 1; i <= this.totalPages; i += 1) {
        const canvas = this.$refs[`pdfCanvas_${i}`][0];
        canvas.dataset.page = i;
        this._observer.observe(canvas);
      }
    },
    async reRenderAllPages() {
      await this.$nextTick();
      await this.renderAllPages();
    },
    scrollToPage(pageNum) {
      const canvasArr = this.$refs[`pdfCanvas_${pageNum}`];
      if (canvasArr && canvasArr[0]) {
        canvasArr[0].scrollIntoView({ behavior: 'smooth' });
      }
    },
    reset() {
      this.rotation = 0
      this.scale = 1
      this.pdfScale = 1.0
      this.workbook = null
      this.currentPage = 1
      this.totalPages = 0
      this.excelData = []
      this.sheetNames = []
      this.currentSheetName = ''
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.scrollToPage(this.currentPage - 1)
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.scrollToPage(this.currentPage + 1)
      }
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown)
    if (this.isExcel && this.fileUrl) {
      this.loadExcelData()
    }
    if(this.isPdf){
      this.loadPdfData();
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
    if (this._observer) this._observer.disconnect()
  }
}
</script>

<style scoped>
.file-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 9999999 !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-preview-container {
  width: 98vw;
  height: 98vh;
  display: flex;
  flex-direction: column;
  /* background: white; */
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.file-preview-header {
  position: relative;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #234357;
  color: white;
  padding: 10px 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.header-title {
  flex: 1;
  max-width: 350px;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
}

.attachment-info {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  cursor: pointer;
  position: relative;
}

.attachment-info[data-tooltip]:hover::before {
  content: attr(data-tooltip);
  position: fixed;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 999999;
  pointer-events: none;
  transform: translateY(100%);
  margin-top: 4px;
  cursor: pointer;
}

.controls {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  /* background: rgba(255, 255, 255, 0.1); */
  padding: 6px;
  border-radius: 6px;
  max-width: 400px;
}

.image-center-controls,
.pdf-center-controls,
.excel-center-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.header-right {
  display: flex;
  gap: 8px;
  flex: 0 0 auto;
}

.text-control-btn {
  border: none;
  background: transparent;
  color: white;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 4px;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.text-control-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.sheet-selector {
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  min-width: 120px;
  cursor: pointer;
  max-width: 200px;
}

.sheet-selector:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
}

.control-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
  font-size: 12px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.control-btn:hover {
  background: white;
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.page-info {
  color: white;
  font-size: 12px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  min-width: 50px;
  justify-content: center;
}

.page-input {
  width: 40px;
  padding: 4px;
  border: none;
  border-radius: 4px;
  text-align: center;
  font-size: 12px;
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  margin-right: 4px;
}

.page-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
}

.file-preview-body {
  flex: 1;
  width: 100%;
  height: calc(100% - 50px);
  overflow: hidden;
  position: relative;
  /* background: #f8f9fa; */
}

.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  /* background: #f8f9fa; */
}

.preview-image {
  max-width: 98%;
  max-height: 98%;
  object-fit: contain;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transform-origin: center center;
}

.pdf-container {
  width: 100%;
  height: 100%;
  background: #f8f9fa;
}

.pdf-scroll-container {
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 5px;
  display: block;
  background: white;
  .pdf-page-canvas {
    display: block;
    margin: 0 auto 8px;
    max-width: 100%;
  }
}

.pdf-viewer {
  display: block;
  width: 100%;
  max-width: none;
  height: auto;
  margin: 0 auto;
  box-shadow: none;
  border: none;
}

.excel-container {
  width: 100%;
  height: 100%;
  background: white;
  padding: 10px;
  overflow: auto;
}

.excel-table {
  border-collapse: collapse;
  width: 100%;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.excel-table td {
  border: 1px solid #dee2e6;
  padding: 12px;
  text-align: left;
  color: #495057;
  background: white;
  font-size: 14px;
}

.excel-table tr:nth-child(even) td {
  background: #f8f9fa;
}

.excel-table th {
  background: #f8f9fa;
  font-weight: bold;
}

.debug-info {
  color: #6c757d;
  text-align: center;
  padding: 50px;
  font-size: 18px;
}

.unsupported-file {
  color: #6c757d;
  text-align: center;
  padding: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.download-btn {
  padding: 12px 24px;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 20px;
  font-size: 14px;
  transition: background 0.2s ease;
}

.download-btn:hover {
  background: #2980b9;
}
</style>